import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'

export type FileViewerSize = 'normal' | 'full'

type FileViewerTokens = {
  border: string
  errorText: string
  errorIconToken: string
}

const tokens: Record<ColorScheme, FileViewerTokens> = {
  dark: {
    border: 'border.subtle',
    errorText: 'text.primary',
    errorIconToken: 'colors.status.error',
  },
  light: {
    border: 'light.border.subtle',
    errorText: 'light.text.primary',
    errorIconToken: 'colors.light.status.error',
  },
}

export const errorIconColor = (colorScheme: ColorScheme): string =>
  tokenVar(tokens[colorScheme].errorIconToken)

/**
 * Body override handed to Modal — centers the media and swaps Modal's text
 * paddings for the viewer's. Longhand pt/px/pb, not the `padding` shorthand:
 * Modal's bodyStyles sets longhands, and Chakra v3 resolves longhands over
 * shorthands regardless of merge order, so a shorthand here silently loses.
 * `minH: 0` + `overflow: hidden` keep the media inside the padded box — the
 * ZoomableImage owns its own scroll when zoomed.
 */
export const fileViewerModalBodyStyles = (
  size: FileViewerSize
): SystemStyleObject => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  minH: 0,
  overflow: 'hidden',
  // Top padding keeps the media clear of the header's control row.
  pt: '24px',
  px: size === 'full' ? '10px' : '20px',
  pb: size === 'full' ? '10px' : '20px',
})

/**
 * The header buttons sit on Modal's bg.card-elevated surface — the same token
 * UtilityButton uses for its resting background, which made them near
 * invisible. bg.card is one step darker, so the buttons read as recessed
 * wells while UtilityButton's lighter hover (bg.tab-active) still pops.
 */
export const fileViewerControlButtonStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  bg: colorScheme === 'light' ? 'light.bg.card' : 'bg.card',
})

export const fileViewerControlsRowStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: '4px',
  // The row fills Modal's header slot and hangs its buttons off the right
  // edge, under the cat-ear close button.
  justifyContent: 'flex-end',
  w: 'full',
}

/**
 * Zoom/pan surface. Token-scrollbars replace the legacy gray.* webkit styles;
 * `scrollbarColor` covers Firefox the same way Modal's body does.
 */
export const zoomableImageContainerStyles = (
  colorScheme: ColorScheme,
  isZoomed: boolean
): SystemStyleObject => ({
  // Definite box (fills the padded body): the zoomed image's percentage width
  // must resolve against a parent whose width does not depend on its content,
  // or CSS treats it as `auto` and the image stays at natural size — which is
  // why zoom looked dead on small images. Block layout while zoomed keeps the
  // scroll origin reachable (flex-centering makes overflow's leading edge
  // unscrollable); flex only centers the fitted, non-overflowing image.
  h: '100%',
  w: '100%',
  ...(isZoomed
    ? { display: 'block' }
    : { alignItems: 'center', display: 'flex', justifyContent: 'center' }),
  overflow: isZoomed ? 'auto' : 'hidden',
  position: 'relative',
  scrollbarColor: `${tokenVar(
    colorScheme === 'light'
      ? 'colors.light.border.strong'
      : 'colors.border.strong'
  )} transparent`,
  '&::-webkit-scrollbar': { height: '8px', width: '8px' },
  '&::-webkit-scrollbar-track': { bg: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    bg: tokens[colorScheme].border,
    borderRadius: '4px',
  },
})

export const zoomableImageStyles = (zoomLevel: number): SystemStyleObject => ({
  display: 'block',
  h: 'auto',
  pointerEvents: 'none',
  transition: 'width 0.2s ease-out, transform 0.2s ease-out',
  userSelect: 'none',
  ...(zoomLevel === 1
    ? {
        maxH: '100%',
        maxW: '100%',
        objectFit: 'contain',
        w: 'auto',
      }
    : { maxH: 'none', maxW: 'none', w: `${zoomLevel * 100}%` }),
})

export const zoomableImageSpinnerStyles: SystemStyleObject = {
  left: '50%',
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
}

export const zoomableImageErrorStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  alignItems: 'center',
  color: tokens[colorScheme].errorText,
  display: 'flex',
  fontSize: 'sm',
  gap: '8px',
  p: '16px',
})
